import { ButtonHTMLAttributes, DetailedHTMLProps, FC, forwardRef } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import { useFormContext, Controller } from 'react-hook-form';
import { getDateOffset, sanitizeDate } from 'utils/convertString';
import { ReactComponent as ChevronIcon } from 'assets/chevron_icon.svg';
import { ReactComponent as StarIcon } from 'assets/star_required_icon.svg';
import { IDatePickerProps } from './types';
import styles from './datePicker.module.scss';

export const DatePickerComponent: FC<IDatePickerProps> = ({
  name,
  placeholder,
  maxDate,
  minDate,
  stylesProp,
  buttonStyleProp,
  label,
  required = false,
  disabled = false,
  noMaxDate = false,
  showRequiredIcon = true,
  ...props
}) => {
  const { control, getFieldState } = useFormContext();

  const CustomInput = forwardRef<
    HTMLButtonElement,
    DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
  >(({ value, onClick }, ref) => (
    <button
      id={name}
      type='button'
      className={`${styles.dateOpener} ${getFieldState(name).error && !value && styles.error}`}
      style={{ ...buttonStyleProp }}
      onClick={onClick}
      ref={ref}
      disabled={disabled}>
      <span className={`${!value && styles.placeholder}`}>{value || placeholder}</span>
      {!disabled && <ChevronIcon />}
    </button>
  ));

  CustomInput.displayName = 'CustomInput';

  return (
    <>
      {label && (
        <div className={styles.labelWrapper}>
          <p className={styles.subtitle}>{label}</p>
          {required && showRequiredIcon ? <StarIcon className={styles.labelRequiredIcon} /> : null}
        </div>
      )}
      <div className={styles.selectBox} style={stylesProp}>
        {!label && required && showRequiredIcon ? <StarIcon className={styles.requiredIcon} /> : null}
        <Controller
          control={control}
          name={name}
          rules={{ required: required }}
          render={({ field: { value, onChange } }) => (
            <>
              <DatePicker
                {...props}
                selected={value && new Date(value)}
                onChange={(date) => {
                  const offset = getDateOffset(date as Date | null);
                  onChange(offset?.slice(0, 10));
                }}
                maxDate={noMaxDate ? undefined : sanitizeDate(maxDate) || new Date()}
                minDate={sanitizeDate(minDate)}
                className={'date-picker'}
                dateFormat='dd.MM.yyyy'
                customInput={<CustomInput />}
                clearButtonClassName={`${styles.clearButton} ${disabled && styles.disabled}`}
                onChangeRaw={(e) => e.preventDefault()}
                scrollableYearDropdown
                yearDropdownItemNumber={30}
                showMonthDropdown
                showYearDropdown
                dropdownMode='select'
                disabled={disabled}
              />
            </>
          )}
        />
      </div>
    </>
  );
};
